import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useMemo } from "react";
import {
    ScheduleLifecycleVerb,
    ScheduleSourceType,
} from "@common/types/domains/accounting/schedule";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useStateAwareNavigate } from "@hooks/useStateAwareNavigate";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { SchedulePicker } from "@components/accounting/schedules/SchedulePicker";
import { filterTemplatesForDocument } from "@components/accounting/schedules/documentScheduleTemplates";
import { resolvePickerTemplates } from "@components/accounting/schedules/schedulePickerTemplates";
import { useAccrualAccountType } from "@components/accounting/schedules/useAccrualAccountType";
import { filterTemplatesForTransaction } from "@components/accounting/schedules/transactionScheduleTemplates";
import { useOpenScheduleJournalEntries } from "@components/accounting/schedules/useOpenScheduleJournalEntries";
import { buildScheduleHubUrl } from "@components/accounting/schedulesReport/schedulesReportDrillDown.lib";
import { useIsMatchableItemLocked } from "../matchLock";
import {
    findMatchableItemApplication,
    getScheduleApplyBlockReason,
    toMatchScheduleCardValues,
} from "../schedule/matchItemSchedule";
import { useMatchScheduleColumn } from "../schedule/matchScheduleColumn.context";
import { useMatchItemScheduleChange } from "../schedule/useMatchItemScheduleChange";
import { MatchItemType, MatchableItem } from "../types";

const MatchScheduleCell = ({ item }: { item: MatchableItem }) => {
    const column = useMatchScheduleColumn();
    const { isViewOnly } = useViewOnlyRefusal();
    const isLocked = useIsMatchableItemLocked(item);
    const isReadonly = isViewOnly || isLocked;
    const navigate = useStateAwareNavigate();
    const { schedulesExpenses: isExpensesEnabled } = useTypedFlags();
    const accountType = useAccrualAccountType(
        item.accrualAccountIds?.length === 1 ? item.accrualAccountIds[0] : null,
    );

    const application = useMemo(
        () =>
            findMatchableItemApplication({
                item,
                applications: column.applications,
            }),
        [column.applications, item],
    );
    const linkedItems = useMemo(() => {
        if (column.sourceItem && item.id === column.sourceItem.id) {
            return column.selectedItems;
        }
        const isSelected = column.selectedItems.some(
            (selected) => selected.id === item.id,
        );
        return isSelected && column.sourceItem ? [column.sourceItem] : [];
    }, [column.selectedItems, column.sourceItem, item.id]);
    const linkBlockReason = useMemo(
        () =>
            getScheduleApplyBlockReason({
                item,
                linkedItems,
                applications: column.applications,
            }),
        [column.applications, item, linkedItems],
    );
    const templates = useMemo(() => {
        const entityTemplates = resolvePickerTemplates({
            templates: column.templates,
            entityId: item.entityId,
        });
        return item.type === MatchItemType.INVOICE
            ? filterTemplatesForDocument({
                  templates: entityTemplates,
                  documentType: ScheduleSourceType.invoice,
                  isExpensesEnabled,
              })
            : filterTemplatesForTransaction({
                  templates: entityTemplates,
                  amount: item.amount,
                  accountType,
                  isExpensesEnabled,
              });
    }, [
        accountType,
        column.templates,
        isExpensesEnabled,
        item.amount,
        item.entityId,
        item.type,
    ]);
    const getBlockReason = useCallback(
        (): string | null => linkBlockReason,
        [linkBlockReason],
    );

    const {
        onSelect,
        onClear,
        confirmModal,
        linkedConflictModal,
        specificDateModal,
        isBusy,
    } = useMatchItemScheduleChange({
        item,
        application,
        showLockedPeriods: column.showLockedPeriods,
    });
    const requestLifecycleAction = column.requestLifecycleAction;
    const onChangeSchedule = useMemo(() => {
        if (
            item.type !== MatchItemType.INVOICE ||
            !application ||
            !requestLifecycleAction
        ) {
            return undefined;
        }
        return () =>
            requestLifecycleAction({
                applicationId: application.id,
                entityId: application.entityId,
                sourceType: ScheduleSourceType.invoice,
                verb: ScheduleLifecycleVerb.change,
                amount: application.amount,
            });
    }, [application, item.type, requestLifecycleAction]);
    const onViewJournalEntries = useOpenScheduleJournalEntries(
        application
            ? {
                  templateId: application.scheduleTemplateId,
                  scheduleApplicationId: application.id,
              }
            : null,
    );

    return (
        <ViewTableCell captureClicks>
            <SchedulePicker
                templates={templates}
                applied={
                    application ? toMatchScheduleCardValues(application) : null
                }
                readonly={isReadonly}
                isDisabled={isBusy}
                getBlockReason={getBlockReason}
                onSelect={onSelect}
                onClear={isReadonly ? undefined : onClear}
                onChangeSchedule={isReadonly ? undefined : onChangeSchedule}
                onViewSchedule={onViewJournalEntries}
                onCreate={() => navigate(buildScheduleHubUrl({}))}
            />
            {confirmModal}
            {linkedConflictModal}
            {specificDateModal}
        </ViewTableCell>
    );
};

export const ScheduleCell = memo(
    ({ row }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const { isEnabled } = useMatchScheduleColumn();
        const item = row.original;
        const isSupported =
            isEnabled &&
            row.depth === 0 &&
            (item.type === MatchItemType.TRANSACTION ||
                item.type === MatchItemType.INVOICE);
        if (!isSupported) {
            return <ViewTableCell />;
        }
        return <MatchScheduleCell item={item} />;
    },
);

ScheduleCell.displayName = "ScheduleCell";
